import React, { useEffect, useState, useRef, Component } from 'react';
import { Menu, X, AlertCircle } from 'lucide-react';
import { ParentSidebarV2, ParentV2Route } from './ParentSidebarV2';
import { useClickOutside } from '../../app/hooks/useClickOutside';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { HomePage } from './pages/HomePage';
import { VoortgangPage } from './pages/VoortgangPage';
import { ToetsenPage } from './pages/ToetsenPage';
import { TipsPage } from './pages/TipsPage';
import { ActivityPage } from './pages/ActivityPage';
import { ComparePage } from './pages/ComparePage';
import { SettingsPage } from './pages/SettingsPage';
import { useAppStore } from '../../app/hooks/useAppStore';
import { BaseErrorBoundary } from '../../components/shared/BaseErrorBoundary';
import { SkeletonPage } from '../../components/shared/Skeleton';
import { DjayAvatar } from '../../mascot';
import { isParentAccount, type AccountsState, type Account, type LearnerAccount } from '../../accounts';
import { getLinkedLearners } from '../../accounts';
import { AppState } from '../../app/types';
import { getParentLastViewedLearner, setParentLastViewedLearner } from '../../data/domains/preferences';
interface ParentPageProps {
  accounts: AccountsState;
  activeAccount: Account;
  linkedLearners: LearnerAccount[];
  selectedChild: LearnerAccount | undefined;
  childData: AppState | null;
  selectedGroup: string;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
  getLearnerData: (learnerId: string) => AppState | null;
  viewingLearnerId: string | null;
  setViewingLearner: (id: string | null) => void;
  linkParentToLearnerById: (parentId: string, learnerId: string) => void;
  onNavigateToLearner?: (learnerId: string) => void;
  onNavigate?: (route: ParentV2Route) => void;
}
const PAGE_MAP: Record<ParentV2Route, ComponentType<Partial<ParentPageProps>>> = {
  home: HomePage,
  voortgang: VoortgangPage,
  toetsen: ToetsenPage,
  tips: TipsPage,
  activiteit: ActivityPage,
  compare: ComparePage as ComponentType<Partial<ParentPageProps>>,
  settings: SettingsPage as ComponentType<Partial<ParentPageProps>>
};
const ROUTE_LABELS: Record<ParentV2Route, string> = {
  home: 'Overzicht',
  voortgang: 'Vakken',
  toetsen: 'Toetsen',
  tips: 'Tips',
  activiteit: 'Activiteit',
  compare: 'Vergelijken',
  settings: 'Instellingen'
};
interface ParentShellV2Props {
  onNavigateToLearner?: (learnerId: string) => void;
  onLogout?: () => void;
  onReturnHome?: () => void;
}
export function ParentShellV2({
  onNavigateToLearner,
  onLogout,
  onReturnHome
}: ParentShellV2Props) {
  const [activeRoute, setActiveRoute] = useState<ParentV2Route>('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  useClickOutside(sidebarRef, () => setMobileMenuOpen(false), mobileMenuOpen);
  useEscapeKey(() => setMobileMenuOpen(false), mobileMenuOpen);
  const {
    accounts,
    activeAccount,
    selectedGroup,
    getLearnerData,
    getLearnerModuleMastery,
    isInitialized,
    setViewingLearner,
    viewingLearnerId,
    linkParentToLearnerById
  } = useAppStore();
  const isParent = activeAccount && isParentAccount(activeAccount);
  const linkedLearners = isParent ? getLinkedLearners(accounts, activeAccount.id) : [];
  // W3-3: Persist "last viewed child" per parent so multi-child Mara
  // returns to the right kid on reload, and parents sharing a device
  // don't overwrite each other's selection.
  useEffect(() => {
    if (!isParent) return;
    const parentId = activeAccount.id;
    let cancelled = false;
    if (viewingLearnerId) {
      // Persist current selection for this parent
      void setParentLastViewedLearner(parentId, viewingLearnerId);
      return;
    }
    if (linkedLearners.length === 0) return;
    if (linkedLearners.length === 1) {
      setViewingLearner(linkedLearners[0].id);
      return;
    }
    // Multiple children: try to restore last viewed
    ;
    (async () => {
      const persistedId = await getParentLastViewedLearner(parentId);
      if (cancelled) return;
      if (persistedId && linkedLearners.some(l => l.id === persistedId)) {
        setViewingLearner(persistedId);
      } else {
        setViewingLearner(linkedLearners[0].id);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isParent, activeAccount, viewingLearnerId, linkedLearners, setViewingLearner]);
  if (!isInitialized) {
    return <div className="flex h-screen bg-slate-50 overflow-hidden font-sans" data-id="element-6999">
        <div className="hidden md:block w-64 bg-white border-r border-slate-200 shrink-0" data-id="element-7000" />
        <main className="flex-1 min-w-0 overflow-auto" data-id="element-7001">
          <SkeletonPage data-id="element-7002" />
        </main>
      </div>;
  }
  if (!isParent) {
    return <div className="flex items-center justify-center min-h-screen bg-slate-50 p-4" data-id="element-7003">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 shadow-sm border border-slate-200 text-center" data-id="element-7004">
          <h2 className="text-2xl font-black text-slate-900 mb-2" data-id="element-7005">
            Geen toegang
          </h2>
          <p className="text-slate-600 mb-8" data-id="element-7006">
            Je bent niet ingelogd als ouder. Gebruik de account switcher om naar
            een ouderaccount te wisselen.
          </p>
          {onReturnHome && <button onClick={onReturnHome} className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors shadow-sm" data-id="element-7007">
              Naar leerlingomgeving
            </button>}
        </div>
      </div>;
  }
  const selectedChild = viewingLearnerId ? linkedLearners.find(l => l.id === viewingLearnerId) : linkedLearners[0];
  const childData = selectedChild ? getLearnerData(selectedChild.id) : null;
  const PageComponent = PAGE_MAP[activeRoute] ?? HomePage;
  return <div className="flex h-screen bg-slate-50 overflow-hidden font-sans" data-id="element-7008">
      {/* Skip to content — a11y */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-emerald-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-bold" data-id="element-7009">
        Ga naar inhoud
      </a>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-slate-200 z-30 flex items-center justify-between px-4 shadow-sm" style={{
      paddingTop: 'max(0.875rem, env(safe-area-inset-top))'
    }} data-id="element-7010">
        <div className="font-bold text-slate-900 flex items-center gap-3 py-3" data-id="element-7011">
          <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center border border-emerald-100 overflow-hidden" data-id="element-7012">
            <DjayAvatar size="xs" emotion="happy" data-id="element-7013" />
          </div>
          <div className="flex flex-col" data-id="element-7014">
            <span className="text-sm font-black leading-tight" data-id="element-7015">
              {ROUTE_LABELS[activeRoute]}
            </span>
            {selectedChild && <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider leading-tight" data-id="element-7016">
                {selectedChild.displayName}
              </span>}
          </div>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 -mr-1 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500" aria-label={mobileMenuOpen ? 'Menu sluiten' : 'Menu openen'} data-id="element-7017">
          {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-7018" /> : <Menu className="w-6 h-6" data-id="element-7019" />}
        </button>
      </div>

      {/* Mobile Sidebar Backdrop — visual only; dismissal handled by useClickOutside + useEscapeKey (Wave 1 F-SHL-2) */}
      <div aria-hidden="true" className={`md:hidden fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-7020" />

      {/* Sidebar */}
      <div ref={sidebarRef} className={`
        fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:z-0 will-change-transform shadow-xl md:shadow-none
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `} style={{
      paddingBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-7021">
        <ParentSidebarV2 activeRoute={activeRoute} onNavigate={route => {
        setActiveRoute(route);
        setMobileMenuOpen(false);
      }} onLogout={onLogout} onReturnHome={onReturnHome} parentName={activeAccount.displayName} linkedLearnersCount={linkedLearners.length} data-id="element-7022" />
      </div>

      {/* Main Content */}
      <main id="main-content" className="flex-1 min-w-0 overflow-auto pt-16 md:pt-0 relative" data-id="element-7023">
        <BaseErrorBoundary logPrefix="Parent Portal" resetKey={activeRoute} fallback={({
        error,
        onReset
      }) => <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center" data-id="element-7025">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4" data-id="element-7026">
                <AlertCircle className="w-8 h-8 text-emerald-600" data-id="element-7027" />
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-2" data-id="element-7028">
                Er ging iets mis
              </h2>
              <p className="text-sm text-slate-500 mb-4 max-w-md" data-id="element-7029">
                {error.message}
              </p>
              <button onClick={onReset} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors" data-id="element-7030">
                Probeer opnieuw
              </button>
            </div>} data-id="element-7024">
          <PageComponent accounts={accounts} activeAccount={activeAccount} linkedLearners={linkedLearners} selectedChild={selectedChild} childData={childData} selectedGroup={selectedGroup.toString()} getLearnerModuleMastery={getLearnerModuleMastery} getLearnerData={getLearnerData} viewingLearnerId={viewingLearnerId} setViewingLearner={setViewingLearner} linkParentToLearnerById={linkParentToLearnerById} onNavigateToLearner={onNavigateToLearner} onNavigate={setActiveRoute} data-id="element-7031" />
        </BaseErrorBoundary>
      </main>
    </div>;
}